/**
 * Parse document title for display: strip leading UUID/hex prefix, leading "a 4dff 8482 ..." style IDs,
 * leading numbers, underscores/hyphens to spaces, strip file extension, strip trailing " (n)" and "[n]".
 * Used by chatbot sources, Clinical References panel, and ChartMind reference links.
 * Kept in a dependency-free util to avoid circular imports (e.g. ReferenceLink -> chatbotMessageUtils -> QueryChatPage).
 *
 * e.g. "1704919000 approach-to-rashes" → "approach to rashes"
 *      "1704978687 Emergency Care Algorithms 2023" → "Emergency Care Algorithms 2023"
 *      "a 4dff 8482 e2ce51426815 Childhood PTB Algorithm Final 16 02 2024[1]" → "Childhood PTB Algorithm Final 16 02 2024"
 */

const UUID_OR_HEX_PREFIX_RE = /^[0-9a-f]+(-[0-9a-f]+)*\s+/i;

/** Strip leading single letter + space + one or more space-separated hex-like tokens (e.g. "a 4dff 8482 e2ce51426815 "). */
const LEADING_ID_PREFIX_RE = /^(?:[a-z]\s+)?(?:[0-9a-f]{4,}(?:\s+[0-9a-f]{4,})*\s+)/i;

export function parseDocumentTitle(name) {
  if (!name || typeof name !== "string") return "";
  let s = name.trim();
  s = s.replace(LEADING_ID_PREFIX_RE, "").trim();
  s = s.replace(UUID_OR_HEX_PREFIX_RE, "").trim();
  s = s.replace(/^\d+[\s_\-.]*/, "").trim();
  s = s.replace(/\.(pdf|docx?|txt|md)$/i, "").trim();
  s = s.replace(/\s*\(\d+\)\s*$/, "").trim();
  s = s.replace(/\s*\[\d+\]\s*$/, "").trim();
  s = s.replace(/_/g, " ").replace(/-/g, " ").trim();
  return s || name.trim();
}

/**
 * Human-readable label for a clinical / RAG reference row.
 * Firestore or vector metadata may store `source` as a string or as a nested object
 * (e.g. { name, description, source }) — never return a plain object for React children.
 *
 * @param {{ fileName?: string, documentName?: string, source?: unknown }} ref
 * @returns {string} Display label; empty string if nothing usable.
 */
export function referenceSourceDisplayLabel(ref) {
  if (!ref || typeof ref !== "object") return "";
  const rawName = ref.fileName ?? ref.documentName;
  const fromFile = parseDocumentTitle(rawName);
  if (fromFile) return fromFile;
  const src = ref.source;
  if (src == null || src === "") return "";
  if (typeof src === "string") return src.trim();
  if (typeof src === "object") {
    if (typeof src.name === "string" && src.name.trim()) return src.name.trim();
    if (typeof src.description === "string" && src.description.trim()) return src.description.trim();
    if (typeof src.source === "string" && src.source.trim()) return src.source.trim();
    try {
      return JSON.stringify(src);
    } catch (e) {
      console.warn("referenceSourceDisplayLabel: stringify failed", e);
      return "";
    }
  }
  return String(src);
}
